import type { IncidentReviewOutcome, IncidentReviewOutcomeRollupItem } from '../../services/incidentsApi';
import { formatCountNoun } from '../copy/countNoun';
import type { CustomerMetric } from '../customerMetrics';
import { customerMetricsSharePopulation } from '../customerMetrics';

export type PatternDispositionSummary = {
  headline: string;
  verifiedNarrative: string | null;
  coverageFact: string | null;
  highSeverityNudge: string | null;
  breakdownLine: string | null;
  newActivityTitle: string;
  historyTitle: string;
  historyLines: string[];
  history: {
    confirmed: number;
    duplicatesCleared: number;
    needingFurtherReview: number;
    pending: number;
  };
};

const CLEARED_REVIEW_OUTCOMES = new Set<IncidentReviewOutcome>([
  'duplicate',
  'false_positive',
  'demo_or_test_activity',
  'expected_activity',
]);

function normalizeOutcome(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function readIncidentReviewOutcome(row: Record<string, unknown> | null | undefined): string {
  if (!row) return '';
  return normalizeOutcome(
    row.review_outcome
    || (row as any)?.safety_case?.review?.review_outcome,
  );
}

function countFromItems(items: IncidentReviewOutcomeRollupItem[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    const key = normalizeOutcome(item?.review_outcome);
    if (!key) continue;
    counts.set(key, (counts.get(key) || 0) + Number(item?.count || 0));
  }
  return counts;
}

function aggregateOutcomeCounts(
  incidents: Array<Record<string, unknown>>,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of incidents) {
    const outcome = readIncidentReviewOutcome(row);
    if (!outcome) continue;
    counts.set(outcome, (counts.get(outcome) || 0) + 1);
  }
  return counts;
}

function sumCounts(counts: Map<string, number>, keys: Iterable<string>): number {
  let total = 0;
  for (const key of Array.from(keys)) {
    total += Number(counts.get(key) || 0);
  }
  return total;
}

function clearedCount(counts: Map<string, number>): number {
  return sumCounts(counts, CLEARED_REVIEW_OUTCOMES);
}

function trueEventCount(counts: Map<string, number>): number {
  return Number(counts.get('true_event') || 0);
}

function needsMoreReviewCount(counts: Map<string, number>): number {
  return Number(counts.get('insufficient_evidence') || 0);
}

function reviewedCount(counts: Map<string, number>): number {
  let total = 0;
  counts.forEach((value) => {
    total += Number(value || 0);
  });
  return total - Number(counts.get('unknown') || 0);
}

function formatObservationLabel(count: number): string {
  return formatCountNoun(count, 'observation');
}

function formatClearedLabel(count: number): string {
  const normalized = Math.max(0, Number(count) || 0);
  if (normalized <= 0) return '';
  return normalized === 1 ? '1 cleared as duplicate' : `${normalized.toLocaleString()} cleared as duplicates`;
}

function formatTrueEventLabel(count: number): string {
  const normalized = Math.max(0, Number(count) || 0);
  if (normalized <= 0) return '';
  return normalized === 1 ? '1 confirmed true event' : `${normalized.toLocaleString()} confirmed true events`;
}

function formatPendingLabel(count: number): string {
  const normalized = Math.max(0, Number(count) || 0);
  return normalized === 1 ? '1 pending' : `${normalized.toLocaleString()} pending`;
}

function formatNeedsMoreReviewLabel(count: number): string {
  const normalized = Math.max(0, Number(count) || 0);
  if (normalized <= 0) return '';
  return normalized === 1 ? '1 needs more review' : `${normalized.toLocaleString()} need more review`;
}

function buildHighSeverityNudge(severity: string | null | undefined): string | null {
  const normalized = String(severity || '').trim().toLowerCase();
  if (normalized !== 'high') return null;
  return 'This is a high-severity pattern — consider spot-checking a few more observations before closing.';
}

export function buildPatternDispositionSummary(args: {
  totalObservations?: number | null;
  outcomeCounts: Map<string, number>;
  severity?: string | null;
  totalPopulationMetric?: CustomerMetric | null;
  reviewedPopulationMetric?: CustomerMetric | null;
  pendingPopulationMetric?: CustomerMetric | null;
}): PatternDispositionSummary | null {
  const reviewed = reviewedCount(args.outcomeCounts);
  const confirmedTrueEvents = trueEventCount(args.outcomeCounts);
  const cleared = clearedCount(args.outcomeCounts);
  const needsMoreReview = needsMoreReviewCount(args.outcomeCounts);
  const totalRaw = Number(args.totalObservations);
  const hasTotal = Number.isFinite(totalRaw) && totalRaw > 0;
  const total = hasTotal ? Math.max(0, totalRaw) : reviewed + needsMoreReview > 0
    ? reviewed
    : 0;
  const ratioPopulationMatched = customerMetricsSharePopulation(
    args.totalPopulationMetric,
    args.reviewedPopulationMetric,
  );

  if (!hasTotal && reviewed <= 0) return null;

  const pendingMetricValue = Number(args.pendingPopulationMetric?.value);
  const hasPendingMetric = Number.isFinite(pendingMetricValue) && pendingMetricValue >= 0;
  const pendingCount = hasPendingMetric
    ? Math.max(0, Math.trunc(pendingMetricValue))
    : hasTotal
      ? Math.max(0, total - reviewed)
      : 0;
  const history = {
    confirmed: confirmedTrueEvents,
    duplicatesCleared: cleared,
    needingFurtherReview: needsMoreReview,
    pending: pendingCount,
  };
  const historyLines = [
    `${history.confirmed.toLocaleString()} confirmed`,
    `${history.duplicatesCleared.toLocaleString()} duplicates/repeats cleared`,
    `${history.needingFurtherReview.toLocaleString()} needing further review`,
    `${history.pending.toLocaleString()} pending`,
  ];

  const headlineParts = [
    hasTotal ? formatObservationLabel(total) : null,
    formatTrueEventLabel(confirmedTrueEvents),
    formatClearedLabel(cleared),
    hasTotal ? formatPendingLabel(pendingCount) : null,
    formatNeedsMoreReviewLabel(needsMoreReview),
  ].filter(Boolean);

  const verifiedNarrative = confirmedTrueEvents > 0 || cleared > 0
    ? [
        confirmedTrueEvents === 1
          ? '1 individually verified'
          : confirmedTrueEvents > 0
            ? `${confirmedTrueEvents.toLocaleString()} individually verified`
            : null,
        cleared === 1
          ? '1 cleared as a repeat of the verified issue'
          : cleared > 0
            ? `${cleared.toLocaleString()} cleared as repeats of the verified issue`
            : null,
      ].filter(Boolean).join(' · ')
    : null;

  const coverageFact = hasTotal && ratioPopulationMatched && reviewed > 0
    ? `${reviewed.toLocaleString()} of ${total.toLocaleString()} reviewed`
    : null;

  return {
    headline: headlineParts.join(' · '),
    verifiedNarrative: verifiedNarrative || null,
    coverageFact: coverageFact || null,
    highSeverityNudge: buildHighSeverityNudge(args.severity),
    breakdownLine: headlineParts.join(' · '),
    newActivityTitle: 'NEW ACTIVITY',
    historyTitle: 'PATTERN REVIEW HISTORY',
    historyLines,
    history,
  };
}

export function buildPatternDispositionSummaryFromRollup(
  rollup: {
    total_incidents?: number | null;
    items?: IncidentReviewOutcomeRollupItem[] | null;
    semantic_metrics?: CustomerMetric[] | null;
  } | null | undefined,
  severity?: string | null,
): PatternDispositionSummary | null {
  if (!rollup) return null;
  const items = Array.isArray(rollup.items) ? rollup.items : [];
  const outcomeCounts = countFromItems(items);
  return buildPatternDispositionSummary({
    totalObservations: rollup.total_incidents,
    outcomeCounts,
    severity,
    totalPopulationMetric: rollup.semantic_metrics?.find((metric) => metric.semantic_key === 'review.total_reviewable_observation_count') || null,
    reviewedPopulationMetric: rollup.semantic_metrics?.find((metric) => metric.semantic_key === 'review.reviewed_observation_count') || null,
    pendingPopulationMetric: rollup.semantic_metrics?.find((metric) => metric.semantic_key === 'review.pending_observation_count') || null,
  });
}

export function buildPatternDispositionSummaryForPatternCase(args: {
  patternScopeIncidents?: Array<Record<string, unknown>> | null;
  rollup?: {
    total_incidents?: number | null;
    items?: IncidentReviewOutcomeRollupItem[] | null;
    semantic_metrics?: CustomerMetric[] | null;
  } | null;
  totalObservations?: number | null;
  severity?: string | null;
}): PatternDispositionSummary | null {
  const incidentCounts = aggregateOutcomeCounts(args.patternScopeIncidents || []);
  const incidentReviewed = reviewedCount(incidentCounts);
  const rollupItems = Array.isArray(args.rollup?.items) ? args.rollup!.items! : [];
  const rollupCounts = countFromItems(rollupItems);
  const rollupReviewed = reviewedCount(rollupCounts);
  const outcomeCounts = rollupReviewed > incidentReviewed ? rollupCounts : incidentCounts;
  return buildPatternDispositionSummary({
    totalObservations: args.rollup?.total_incidents ?? args.totalObservations,
    outcomeCounts,
    severity: args.severity,
    totalPopulationMetric: args.rollup?.semantic_metrics?.find((metric) => metric.semantic_key === 'review.total_reviewable_observation_count') || null,
    reviewedPopulationMetric: args.rollup?.semantic_metrics?.find((metric) => metric.semantic_key === 'review.reviewed_observation_count') || null,
    pendingPopulationMetric: args.rollup?.semantic_metrics?.find((metric) => metric.semantic_key === 'review.pending_observation_count') || null,
  });
}

export function buildPatternDispositionSummaryFromIncidents(
  incidents: Array<Record<string, unknown>>,
  options?: {
    totalObservations?: number | null;
    severity?: string | null;
  },
): PatternDispositionSummary | null {
  if (!Array.isArray(incidents) || incidents.length <= 0) {
    if (!options?.totalObservations) return null;
    return buildPatternDispositionSummary({
      totalObservations: options.totalObservations,
      outcomeCounts: new Map(),
      severity: options.severity,
    });
  }
  return buildPatternDispositionSummary({
    totalObservations: options?.totalObservations ?? incidents.length,
    outcomeCounts: aggregateOutcomeCounts(incidents),
    severity: options?.severity,
  });
}

export function filterIncidentsToPatternScope(
  incidents: Array<Record<string, unknown>>,
  scope: Record<string, unknown> | null | undefined,
): Array<Record<string, unknown>> {
  if (!scope || !Array.isArray(incidents)) return [];
  const incidentType = String(scope.incident_type || '').trim();
  const zoneId = String(scope.zone_id || '').trim();
  const cameraId = String(scope.camera_id || '').trim();
  const deviceId = String(scope.device_id || '').trim();
  return incidents.filter((row) => {
    if (incidentType && String(row?.incident_type || '').trim() !== incidentType) return false;
    if (zoneId && String(row?.zone_id || '').trim() !== zoneId) return false;
    if (cameraId && String(row?.camera_id || '').trim() !== cameraId) return false;
    if (deviceId && String(row?.device_id || '').trim() !== deviceId) return false;
    return true;
  });
}
